import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { DatePipe } from '@angular/common';

import { QuicklinkModule } from 'ngx-quicklink';
import { SharedModule } from './shared.module';

import { Config } from './config';

// SW
import { ServiceWorkerModule } from '@angular/service-worker';

// JWT
import { JwtModule } from '@auth0/angular-jwt';

export function tokenGetter() {
  return localStorage.getItem('mis.vrm.eDriving.access_token');
}

// NG-IDLE & Keep-alive
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';

// ngx - Translations
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { VrmLangTranslationLoader } from './interceptors-and-loaders/language.loader';

export function HttpLoaderFactory(httpClient: HttpClient) {
  return new VrmLangTranslationLoader(httpClient);
}

// App core
import { AppComponent } from './app.component';
import { LoginComponent } from './pages/login/login.component';
import { SingleSignOnComponent } from './pages/sso/single-sign-on.component';

// Reports
import { CommonReportsComponent } from './pages/common-reports/common-reports.component';

// Activation
import { FleetActivationComponent } from './pages/fleet-activation/fleet-activation.component';

// Plug-Ins | Components | Interceptors | Pipes
import { AgGridModule } from 'ag-grid-angular';
import 'ag-grid-enterprise';
import 'ag-grid-enterprise/chartsModule'; // NOTE: Do not import if not in use . Charts is in Beta
import { HttpRequestInterceptorProvider } from './interceptors-and-loaders/http.interceptor';
import { ImgFallbackModule } from 'ngx-img-fallback';
import { PdfViewerModule } from 'ng2-pdf-viewer';

import { VrmMatPaginatorComponent } from './components/vrm-mat-paginator/vrm-mat-paginator.component';
import { VrmCardComponent } from './components/vrm-card/vrm-card.component';
import { VrmChartJsComponent } from './components/vrm-chart-js/vrm-chart-js.component';
import { VrmExpansionPanelComponent } from './components/vrm-expansion-panel/vrm-expansion-panel.component';
import { VrmSummaryCardComponent } from './components/vrm-summary-card/vrm-summary-card.component';
import { VrmSummaryItemComponent } from './components/vrm-summary-card/vrm-summary-item.component';
import { VrmIndexOverrideComponent } from './components/vrm-index-override/vrm-index-override.component';
import { VrmTrainingActivityComponent } from './components/vrm-training-activity/vrm-training-activity.component';
import { VrmMentorRewardsSummaryComponent } from './components/vrm-mentor-rewards-summary/vrm-mentor-rewards-summary.component';
import { VrmMapComponent } from './components/vrm-map/vrm-map.component';
import { VrmFusionChartComponent } from './components/vrm-fusion-chart/vrm-fusion-chart.component';

import { PageHeaderComponent } from './components/page-header/page-header.component';
import { VrmPercentageArcComponent } from './components/vrm-percentage-arc/vrm-percentage-arc.component';

import { RoadRiskFullInfoComponent } from './components/road-risk-full-info/road-risk-full-info.component';

import { CheckUpdatesButtonComponent } from './components/xs-components/check-updates-button.component';
import { DarkThemeToggleComponent } from './components/xs-components/dark-theme-toggle.component';
import { SettingsButtonComponent } from './components/xs-components/settings-button.component';
import { LogoutButtonComponent } from './components/xs-components/logout-button.component';
import { CalendarHeaderComponent } from './components/xs-components/calendar-header.component';
import { SimpleClientPaginatorComponent } from './components/xs-components/simple-client-paginator.component';
import { CrossPageInterventionRendererComponent } from './components/cross-page-intervention-renderer/cross-page-intervention-renderer.component';
import { TrademarkRendererComponent } from './components/xs-components/trademark-renderer.component';
import { RiskRatingIndicationBarComponent } from './components/xs-components/risk-rating-indication-bar.component';
import { RiskRatingComparisonWidgetComponent } from './components/xs-components/risk-rating-comparison-widget.component';
import { TooltipBadgeComponent } from './components/xs-components/tooltip-badge.component';
import { TooltipViewerComponent } from './components/xs-components/tooltip-viewer.component';

import { ColumnNamesTransform } from './pipes/get-column-names-from-meta.pipe';
import { TransformColNames } from './pipes/transform-col-names';
import { TransformReportCell } from './pipes/transform-report-cell';
import { DetermineElementProps } from './pipes/determine-element-props.pipe';
import { DetermineReportCellColor } from './pipes/determine-report-cell-color';
import { ClassBasedOnValue } from './pipes/classBasedOnValue';
import { NumberToDecimalPlacesPipe } from './pipes/number-to-decimal-places.pipe';
import { GetNavTitlePipe } from './pipes/get-nav-title.pipe';
import { TranslateOrFallbackPipe } from './pipes/translate-or-fallback.pipe';

import { SnackBarFromComponentComponent } from './components/snack-bar-from-component/snack-bar-from-component.component';
import { CountdownDirective } from './directives/countdown.directive';

import { RenderComponentDirective } from './directives/render-component.directive';

import { VrmIndexingRatingBgDirective } from './directives/indexing-rating-bg-color.directive';
import { VrmCourseStatusColorDirective } from './directives/colors/vrm-course-status-color.directive';

import { NamesToInitial } from './pipes/names-to-initial.pipe';
import { TrustAsSafeUrl } from './pipes/trust-as-safe-url.pipe';
import { DriverIndexComponent } from './dialogs/driver-index/driver-index.component';
import { DriverIndexClassicComponent } from './dialogs/driver-index-classic/driver-index-classic.component';
import { PromptWithActionDialogComponent } from './dialogs/prompt-with-action/prompt-with-action.component';
import { FilterSelectionDialogComponent } from './dialogs/filter-selection/filter-selection.component';
import { ImageViewerComponent } from './dialogs/image-viewer/image-viewer.component';
import { MisNotificationsComponent } from './dialogs/mis-notifications/mis-notifications.component';
import { CustomContentAdminComponent } from './dialogs/custom-content-admin/custom-content-admin.component';

import { ProfileInsightDetailsComponent } from './dialogs/profile-insight-details/profile-insight-details.component';
import { CounterBadgeComponent } from './components/xs-components/counter-badge.component';
import { GuideViewerComponent } from './dialogs/guide-viewer/guide-viewer.component';
import { GuideViewerSidepaneComponent } from './dialogs/guide-viewer-sidepane/guide-viewer-sidepane.component';
import { DialogSidepaneComponent } from './dialogs/sidepane/dialog-sidepane.component';
import { UploadOutcomeComponent } from './dialogs/upload-user-outcome/upload-outcome.component';

import { SendActivationMailComponent } from './components/vrm-mentor-reports/send-activation.component';

import { GoodAveragePoorCardComponent } from './components/good-average-poor-card/good-average-poor-card.component';
import { environment } from '../environments/environment';
import { MenuNotificationBadgeComponent } from './components/menu-notification-badge/menu-notification-badge';
import { SelectUserComponent } from './components/vrm-mentor-reports/select-user.component';
import { ManagerGroupComponent } from './components/vrm-mentor-reports/manager-groups.component';
import { VrmTooltipComponent } from './components/vrm-tooltip/vrm-tooltip.component';
import { AngularMultiSelectModule } from 'angular2-multiselect-dropdown';
import { LoginLogoutNotificationBadgeComponent } from './components/login-logout-notification-badge/login-logout-notification-badge.component';
import { VrmAnalyticsChartFilterComponent } from './components/vrm-analytics-chart-filter/vrm-analytics-chart-filter.component';
import { VrmVideoPlayerComponent } from './components/vrm-video-player/vrm-video-player.component';
import { LoginWithV2tokenComponent } from './pages/login-with-v2token/login-with-v2token.component';
import { GridShortTripFlagComponent } from './components/ag-grid-cell-renderers/grid-short-trip-flag.component';
import { NumberToPercentageComponent } from './components/ag-grid-cell-renderers/number-to-percentage.component';
import { NumberToDecimalPlaceComponent } from './components/ag-grid-cell-renderers/number-to-decimal-place.component';
import { PrefixPlusSignToPositiveNumComponent } from './components/ag-grid-cell-renderers/prefix-plus-sign-to-positive-num.component';
import { MaskPoorAverageGoodRatingComponent } from './components/ag-grid-cell-renderers/mask-poor-average-good-rating.component';
import { MaskStandardHighMedLowRatingsComponent } from './components/ag-grid-cell-renderers/mask-standard-high-med-low-ratings.component';
import { MaskCompleteIncompleteNotstartedRatingComponent } from './components/ag-grid-cell-renderers/mask-complete-incomplete-notstarted-rating.component';
import { MaskCompanyRiskLevelPointsComponent } from './components/ag-grid-cell-renderers/mask-company-risk-level-points.component';
import { MaskCompanyRiskLevelScoreComponent } from './components/ag-grid-cell-renderers/mask-company-risk-level-score.component';
import { FileExtToIconAndLinkButtonComponent } from './components/ag-grid-cell-renderers/file-ext-to-icon-and-link-button.component';
import { SwitchCompanyLinkButtonComponent } from './components/ag-grid-cell-renderers/switch-company-link-button.component';
import { RedirectAndSwitchCompanyButtonComponent } from './components/ag-grid-cell-renderers/redirect-and-switch-company-button.component';
import { ViewImageButtonComponent } from './components/ag-grid-cell-renderers/view-image-button.component';
import { AdminRowOperationsComponent } from './components/ag-grid-cell-renderers/admin-row-operations.component';
import { GridIndexingCoachLinkButtonComponent } from './components/ag-grid-cell-renderers/grid-indexing-coach-link-button.component';
import { GridSeeVirtualSafetyDiscussionLinkButtonComponent } from './components/ag-grid-cell-renderers/grid-see-virtual-safety-discussion-link-button.component';
import { TitleLinkButtonCasualReportComponent } from './components/ag-grid-cell-renderers/title-link-button-casual-report.component';
import { ActionOptionButtonCasualReportComponent } from './components/ag-grid-cell-renderers/action-option-button-casual-report.component';
import { SendFileActionButtonComponent } from './components/ag-grid-cell-renderers/send-file-action-button-casual-report.component';
import { OpenResourceInNewTabOrWindowComponent } from './components/ag-grid-cell-renderers/open-resource-in-new-tab-win.component';
import { FileExtensionAsIconComponent } from './components/ag-grid-cell-renderers/file-extension-as-icon.component';
import { GridCustomReportDownloadButtonComponent } from './components/ag-grid-cell-renderers/grid-custom-report-download-button.component';
import { GridSeeMvrLinkButtonComponent } from './components/ag-grid-cell-renderers/grid-see-mvr-link-button.component';
import { GridSeeLicenceCheckLinkButton } from './components/ag-grid-cell-renderers/grid-see-licence-check-link-button.component';
import { GridSeeOneToOneLinkButtonComponent } from './components/ag-grid-cell-renderers/grid-see-one-to-one-link-button.component';
import { GridSeePccLinkButtonComponent } from './components/ag-grid-cell-renderers/grid-see-pcc-link-button.component';
import { GridOrderMvrLinkButtonComponent } from './components/ag-grid-cell-renderers/grid-order-mvr-link-button.component';
import { GridAlertActionRequiredButtonComponent } from './components/ag-grid-cell-renderers/grid-alert-action-required-button.component';
import { PinnedRowStyleComponent } from './components/ag-grid-cell-renderers/pinned-row-style.component';
import { GridViewPopupFormButtonComponent } from './components/ag-grid-cell-renderers/grid-view-popup-form-button.component';
import { CoachingRecommendationActionsComponent } from './components/ag-grid-cell-renderers/coaching-recommendation-actions.component';
import { CustomDrivingRatingFilterComponent } from './components/ag-grid-custom-filters/custom-driving-rating-filter.component';
import { CustomCompanyRiskFilterComponent } from './components/ag-grid-custom-filters/custom-company-risk-filter.component';
import { CustomStandardHighMedLowRatingComponent } from './components/ag-grid-custom-filters/custom-standard-high-med-low-rating-filter.component';
import { CustomCompleteIncompleteNotstartedRatingFilterComponent } from './components/ag-grid-custom-filters/custom-complete-incomplete-notstarted-rating-filter.component';
import { CustomSettingsToolPanelComponent } from './components/ag-grid-components/settings-tool-pane/custom-settings-tool-panel.component';
import { CustomDetailsToolPaneComponent } from './components/ag-grid-components/details-tool-pane/custom-details-tool-pane.component';
import { CustomMoreDetailsSeverityComponent } from './components/ag-grid-components/custom-more-details-severity/custom-more-details-severity.component';
import { StationDspFilterComponent } from './components/ag-grid-components/station-dsp-filter/station-dsp-filter.component';
import { StatusBarRecommendationActionsComponent } from './components/ag-grid-components/status-bar-recommendation-actions/status-bar-recommendation-actions.component';
import { CustomStatusBarRowCountComponent } from './components/ag-grid-components/custom-status-bar-counters/custom-status-bar-row-count.component';
import { CustomStatusBarFilteredCountComponent } from './components/ag-grid-components/custom-status-bar-counters/custom-status-bar-filtered-count.component';
import { CustomStatusBarSelectedCountComponent } from './components/ag-grid-components/custom-status-bar-counters/custom-status-bar-selected-count.component';
import { LicenceCategoriesTooltipsComponent } from './components/ag-grid-cell-renderers/licence-categories-tooltips.component';
import { ComposedCourseStatusComponent } from './components/ag-grid-cell-renderers/composed-course-status.component';
import { CellTooltipsComponent } from './components/ag-grid-cell-renderers/cell-tooltips.component';
import { CellStaticTooltipsComponent } from './components/ag-grid-cell-renderers/cell-static-tooltips.component';
import { PopupFormComponent } from './components/forms/popup-form/popup-form.component';
import { GalleryContainerComponent } from './components/vrm-gallery/gallery-container/gallery-container.component';
import { GalleryItemComponent } from './components/vrm-gallery/gallery-item/gallery-item.component';
import { GalleryItemDetailComponent } from './components/vrm-gallery/gallery-item-detail/gallery-item-detail.component';
import { GridViewCreateClaimButtonComponent } from './components/ag-grid-cell-renderers/grid-view-create-claim-button.component';
import { GridDownloadReportFileButtonComponent } from './components/ag-grid-cell-renderers/grid-download-report-file-button.component';
import { CellGicDamageTypeComponent } from './components/ag-grid-cell-renderers/cell-gic-damage-type.component';
import { CustomAdminRowOperationsFilterComponent } from './components/ag-grid-custom-filters/custom-admin-row-operations-filter.component';
import { CognitoSsoComponent } from './pages/cognito-sso/cognito-sso.component';
import { SupervisionSsoComponent } from './pages/supervision-sso/supervision-sso.component';

const productionOnlyProviders = environment.production || environment.staging ? [] : [];

const columnClasses = [
  CoursesColumns,
  DriverColumns,
  MentorColumns,
  LicenceColumns,
  AlertsColumns,
  RoadRiskColumns,
  DriverImportColumns,
];

import { FusionChartsModule } from 'angular-fusioncharts';

// Import FusionCharts library and chart modules
import * as FusionCharts from 'fusioncharts';
import * as charts from 'fusioncharts/fusioncharts.charts';
import * as FusionTheme from 'fusioncharts/themes/fusioncharts.theme.fusion';
import { CoursesColumns } from './columns/courses.columns';
import { DriverColumns } from './columns/driver.columns';
import { MentorColumns } from './columns/mentor.columns';
import { LicenceColumns } from './columns/licence.columns';
import { AlertsColumns } from './columns/alerts.columns';
import { RoadRiskColumns } from './columns/roadrisk.columns';
import { LicenceResultsModalComponent } from './components/smart/licence-results-modal/licence-results-modal.component';
import { DriverLicenceSummaryComponent } from './components/presentational/driver-licence-summary/driver-licence-summary.component';
import { LicenceEndorsementsTableComponent } from './components/presentational/licence-endorsements-table/licence-endorsements-table.component';
import { LicenceEntitlementsTableComponent } from './components/presentational/licence-entitlements-table/licence-entitlements-table.component';
import { LicenceRestrictionsTableComponent } from './components/presentational/licence-restrictions-table/licence-restrictions-table.component';
import { ViewLicenceResultsModalButtonComponent } from './components/smart/view-licence-results-modal-button/view-licence-results-modal-button.component';
import { BasicTableComponent } from './components/presentational/basic-table/basic-table.component';
import { AuthenticationModule } from './modules/authentication/authentication.module';

import { FileUploaderComponent } from './components/file-uploader/file-uploader.component';
import { DriverImportComponent } from './pages/driver-import/driver-import.component';

import { ViewDetailRendererComponent } from './pages/driver-import/driver-import-grid/view-detail-renderer.component';
import { ProcessedFileRendererComponent } from './pages/driver-import/driver-import-grid/processed-file-renderer.component';
import { DriverImportConfirmationComponent } from './pages/driver-import/dialogs/driver-import-confirmation.component';
import { ImportConfigurationDialogComponent } from './pages/driver-import/dialogs/import-configuration-dialog.component';
import { ImportDetailDialogComponent } from './pages/driver-import/dialogs/import-detail-dialog.component';
import { ImportJobStatusComponent } from './pages/driver-import/dialogs/import-job-status.component';
import { VrmDriverImportGridComponent } from './pages/driver-import/driver-import-grid/vrm-driver-import-grid.component';
import { ImportStatusRendererComponent } from './pages/driver-import/driver-import-grid/import-status-renderer.component';
import { DriverImportGridHeaderComponent } from './pages/driver-import/driver-import-grid/driver-import-grid-header/driver-import-grid-header.component';
import { MappingTableComponent } from './pages/driver-import/dialogs/mapping-table.component';
import { DriverImportColumns } from './columns/driverImport.columns';

// Pass the fusioncharts library and chart modules
FusionChartsModule.fcRoot(FusionCharts, charts, FusionTheme);
FusionCharts.options['license']({
  key: Config.fusionchartsLicense,
  creditLabel: false,
});

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    SingleSignOnComponent,
    VrmChartJsComponent,
    VrmCardComponent,
    VrmMatPaginatorComponent,
    VrmExpansionPanelComponent,
    FleetActivationComponent,
    CommonReportsComponent,

    VrmSummaryCardComponent,
    VrmSummaryItemComponent,
    VrmIndexOverrideComponent,
    VrmTrainingActivityComponent,
    VrmMentorRewardsSummaryComponent,
    VrmMapComponent,

    SnackBarFromComponentComponent,
    VrmPercentageArcComponent,
    GoodAveragePoorCardComponent,

    RoadRiskFullInfoComponent,

    CheckUpdatesButtonComponent,
    DarkThemeToggleComponent,
    SettingsButtonComponent,
    LogoutButtonComponent,
    CalendarHeaderComponent,
    MenuNotificationBadgeComponent,
    SimpleClientPaginatorComponent,
    CrossPageInterventionRendererComponent,
    TrademarkRendererComponent,
    RiskRatingIndicationBarComponent,
    RiskRatingComparisonWidgetComponent,
    TooltipBadgeComponent,
    TooltipViewerComponent,

    SendActivationMailComponent,
    ViewDetailRendererComponent,
    ProcessedFileRendererComponent,
    ImportStatusRendererComponent,
    ColumnNamesTransform,
    TransformColNames,
    TransformReportCell,
    DetermineElementProps,
    DetermineReportCellColor,
    ClassBasedOnValue,
    CountdownDirective,
    VrmIndexingRatingBgDirective,
    VrmCourseStatusColorDirective,
    TrustAsSafeUrl,
    NamesToInitial,
    DriverIndexComponent,
    DriverIndexClassicComponent,
    PromptWithActionDialogComponent,
    FilterSelectionDialogComponent,
    ImageViewerComponent,
    MisNotificationsComponent,
    DriverImportConfirmationComponent,
    CustomContentAdminComponent,
    ProfileInsightDetailsComponent,
    CounterBadgeComponent,
    GuideViewerComponent,
    GuideViewerSidepaneComponent,

    RenderComponentDirective,

    UploadOutcomeComponent,

    DialogSidepaneComponent,
    PageHeaderComponent,
    NumberToDecimalPlacesPipe,
    GetNavTitlePipe,
    TranslateOrFallbackPipe,

    // Ag-Grid Cell Renderers / sub components
    NumberToPercentageComponent,
    NumberToDecimalPlaceComponent,
    CustomDrivingRatingFilterComponent,
    CustomCompanyRiskFilterComponent,
    CustomStandardHighMedLowRatingComponent,
    CustomCompleteIncompleteNotstartedRatingFilterComponent,
    PrefixPlusSignToPositiveNumComponent,
    MaskPoorAverageGoodRatingComponent,
    MaskStandardHighMedLowRatingsComponent,
    MaskCompleteIncompleteNotstartedRatingComponent,
    MaskCompanyRiskLevelPointsComponent,
    MaskCompanyRiskLevelScoreComponent,
    FileExtToIconAndLinkButtonComponent,
    SwitchCompanyLinkButtonComponent,
    RedirectAndSwitchCompanyButtonComponent,
    ViewImageButtonComponent,
    AdminRowOperationsComponent,
    CustomSettingsToolPanelComponent,
    CustomDetailsToolPaneComponent,
    CustomMoreDetailsSeverityComponent,
    StationDspFilterComponent,
    StatusBarRecommendationActionsComponent,
    CustomStatusBarRowCountComponent,
    CustomStatusBarFilteredCountComponent,
    CustomStatusBarSelectedCountComponent,
    GridIndexingCoachLinkButtonComponent,
    GridSeeVirtualSafetyDiscussionLinkButtonComponent,
    TitleLinkButtonCasualReportComponent,
    ActionOptionButtonCasualReportComponent,
    SendFileActionButtonComponent,
    OpenResourceInNewTabOrWindowComponent,
    FileExtensionAsIconComponent,
    GridSeeMvrLinkButtonComponent,
    GridSeeLicenceCheckLinkButton,
    GridSeeOneToOneLinkButtonComponent,
    GridSeePccLinkButtonComponent,
    GridCustomReportDownloadButtonComponent,
    GridOrderMvrLinkButtonComponent,
    GridViewPopupFormButtonComponent,
    GridAlertActionRequiredButtonComponent,
    LicenceCategoriesTooltipsComponent,
    ComposedCourseStatusComponent,
    CellTooltipsComponent,
    CellStaticTooltipsComponent,
    CoachingRecommendationActionsComponent,
    PinnedRowStyleComponent,
    GridViewCreateClaimButtonComponent,
    GridDownloadReportFileButtonComponent,
    CellGicDamageTypeComponent,

    // ----------------------
    SelectUserComponent,
    ManagerGroupComponent,
    LoginLogoutNotificationBadgeComponent,
    VrmTooltipComponent,
    VrmAnalyticsChartFilterComponent,
    CustomAdminRowOperationsFilterComponent,

    VrmVideoPlayerComponent,
    LoginWithV2tokenComponent,
    GridShortTripFlagComponent,
    PopupFormComponent,
    GalleryContainerComponent,
    GalleryItemComponent,
    GalleryItemDetailComponent,
    VrmFusionChartComponent,
    LicenceResultsModalComponent,
    DriverLicenceSummaryComponent,
    LicenceEndorsementsTableComponent,
    LicenceEntitlementsTableComponent,
    LicenceRestrictionsTableComponent,
    ViewLicenceResultsModalButtonComponent,
    BasicTableComponent,
    CognitoSsoComponent,
    SupervisionSsoComponent,
    FileUploaderComponent,
    VrmDriverImportGridComponent,
    DriverImportComponent,
    ImportConfigurationDialogComponent,
    ImportDetailDialogComponent,
    ImportJobStatusComponent,
    DriverImportGridHeaderComponent,
    MappingTableComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    QuicklinkModule,
    AppRoutingModule,
    AngularMultiSelectModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },

      isolate: false,
    }),

    // JWT
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
        allowedDomains: [Config.apiRoot.uri],
        disallowedRoutes: [`${Config.apiRoot.uri}/api/login`],
      },
    }),
    // NG-IDLE & Keepalive
    NgIdleKeepaliveModule.forRoot(),

    // Plug-Ins
    AgGridModule,

    ImgFallbackModule,
    PdfViewerModule,

    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production || environment.staging || environment.preProduction,
    }),
    FusionChartsModule,
    AuthenticationModule,
  ],
  providers: [HttpRequestInterceptorProvider, ...productionOnlyProviders, ...columnClasses, DatePipe],
  bootstrap: [AppComponent],
  exports: [],
})
export class AppModule {}
